import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Star, ArrowLeft, Check, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReviewPage() {
  const { bookingId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("t") || "";

  const [status, setStatus] = useState("loading");
  const [errorMsg, setErrorMsg] = useState("");
  const [info, setInfo] = useState(null);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [nps, setNps] = useState(null);
  const [comment, setComment] = useState("");
  const [publicOk, setPublicOk] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setErrorMsg("Missing review token in link.");
      setStatus("error");
      return;
    }
    axios
      .get(`${API}/review/${bookingId}/info`, { params: { t: token } })
      .then((res) => {
        setInfo(res.data);
        setStatus(res.data.already_submitted ? "done" : "ready");
      })
      .catch((err) => {
        setErrorMsg(err.response?.data?.detail || "This review link is no longer valid.");
        setStatus("error");
      });
  }, [bookingId, token]);

  const submit = async () => {
    if (rating < 1) {
      toast.error("Please pick a star rating");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/review/${bookingId}/submit`, {
        t: token,
        rating,
        nps: nps === null ? null : Number(nps),
        comment: comment.trim() || null,
        public_ok: publicOk,
      });
      setStatus("done");
      toast.success("Thanks — feedback captured.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit the review");
    } finally {
      setSubmitting(false);
    }
  };

  if (status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" />
      </div>
    );
  }
  if (status === "error") {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-md border border-red-500/30 bg-red-500/[0.05] p-8 text-center" data-testid="review-error">
          <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-4" />
          <h1 className="font-serif text-2xl text-white mb-3">Cannot leave a review</h1>
          <p className="text-slate-300 text-sm leading-relaxed mb-6">{errorMsg}</p>
          <a href="/" className="inline-flex items-center gap-2 border border-slate-700 text-slate-300 px-5 py-2.5 text-xs uppercase tracking-widest hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">
            <ArrowLeft className="w-3 h-3" /> Back to Bad Boss Clinic
          </a>
        </div>
      </div>
    );
  }
  if (status === "done") {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-md border border-[#D4AF37]/40 bg-[#D4AF37]/[0.05] p-8 text-center" data-testid="review-done">
          <div className="inline-flex items-center justify-center w-14 h-14 border border-[#D4AF37] bg-[#D4AF37]/20 mb-4">
            <Check className="w-7 h-7 text-[#D4AF37]" />
          </div>
          <h1 className="font-serif text-2xl text-white mb-3">Thanks for the feedback</h1>
          <p className="text-slate-300 text-sm mb-6">
            {info?.already_submitted
              ? "You've already submitted a review for this session."
              : "Every piece of client feedback goes to the clinical team. If you flagged your comment as OK to publish, we'll reach out before posting anything."}
          </p>
          <a href="/" className="inline-flex items-center gap-2 border border-slate-700 text-slate-300 px-5 py-2.5 text-xs uppercase tracking-widest hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">
            <ArrowLeft className="w-3 h-3" /> Back to Bad Boss Clinic
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-4" data-testid="review-page">
      <div className="max-w-xl mx-auto">
        <a href="/" className="inline-flex items-center gap-2 text-slate-400 hover:text-white text-xs uppercase tracking-widest mb-6 transition-colors">
          <ArrowLeft className="w-3 h-3" /> Bad Boss Clinic
        </a>
        <div className="border border-slate-800 bg-slate-900/50 p-6 md:p-8">
          <p className="text-[10px] uppercase tracking-[0.24em] text-[#D4AF37] mb-2">Session review</p>
          <h1 className="font-serif text-3xl text-white mb-4">How did your session go?</h1>
          <p className="text-slate-500 text-sm mb-6">
            {info.date} · {info.start_time} UTC · {info.session_type === "initial" ? "Initial consultation" : "Follow-up"} with {info.scientist}
          </p>

          <div className="space-y-6">
            <div>
              <p className="text-slate-400 text-xs uppercase tracking-widest mb-3">Your rating</p>
              <div className="flex gap-2" data-testid="review-stars" onMouseLeave={() => setHoverRating(0)}>
                {[1, 2, 3, 4, 5].map((n) => {
                  const active = (hoverRating || rating) >= n;
                  return (
                    <button
                      key={n}
                      type="button"
                      onMouseEnter={() => setHoverRating(n)}
                      onClick={() => setRating(n)}
                      data-testid={`review-star-${n}`}
                      className="transition-colors"
                      aria-label={`${n} star${n === 1 ? "" : "s"}`}
                    >
                      <Star size={30} className={active ? "text-[#D4AF37] fill-[#D4AF37]" : "text-slate-700"} />
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <p className="text-slate-400 text-xs uppercase tracking-widest mb-3">
                How likely are you to recommend us? (0 = not at all, 10 = definitely)
              </p>
              <div className="flex flex-wrap gap-1.5" data-testid="review-nps">
                {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setNps(nps === n ? null : n)}
                    data-testid={`review-nps-${n}`}
                    className={`w-9 h-9 text-sm border transition-colors ${
                      nps === n
                        ? n >= 9
                          ? "border-green-500 bg-green-500/20 text-green-400"
                          : n >= 7
                          ? "border-[#D4AF37] bg-[#D4AF37]/15 text-[#D4AF37]"
                          : "border-red-500 bg-red-500/15 text-red-400"
                        : "border-slate-700 text-slate-400 hover:border-slate-500"
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="text-slate-400 text-xs uppercase tracking-widest mb-3">Comment (optional)</p>
              <textarea
                data-testid="review-comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={4}
                maxLength={2000}
                placeholder="What worked, what didn't, or anything you'd tell someone thinking about booking."
                className="w-full bg-slate-950 border border-slate-800 focus:border-[#D4AF37]/60 outline-none text-slate-200 text-sm p-3"
              />
            </div>

            <label className="flex items-start gap-3 cursor-pointer" data-testid="review-public-toggle">
              <input
                type="checkbox"
                checked={publicOk}
                onChange={(e) => setPublicOk(e.target.checked)}
                className="mt-1 accent-[#D4AF37]"
              />
              <span className="text-slate-400 text-xs leading-relaxed">
                It's OK to publish this review (first name + your rating + comment) on the Bad Boss Clinic website.
                We'll always check with you before publishing.
              </span>
            </label>

            <div className="pt-2 flex justify-end">
              <button
                onClick={submit}
                disabled={submitting || rating < 1}
                data-testid="review-submit-btn"
                className="inline-flex items-center gap-2 bg-[#D4AF37] text-slate-950 px-6 py-3 text-xs uppercase tracking-widest font-medium hover:bg-[#B5952F] disabled:opacity-50 transition-colors"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                Submit review
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
